/**
 * bunkerDemoScenario.ts
 *
 * Decoupled scenario data for {@link BunkerProductDemo}. Each scenario is split
 * into two halves:
 *
 *   - {@link ScenarioMeta} — the NON-translatable structural data (citation
 *     numbers, viewer page/highlightLine/marker, verified confidence %). Lives
 *     in code, in {@link SCENARIO_META}.
 *   - the translatable strings (chatTitle / question / thinkingSteps / answer /
 *     citation document+highlight / viewer title+lines / verifiedBadge) live in
 *     the `demo` i18next namespace under `scenarios.<id>.*`.
 *
 * {@link useScenario} assembles a full {@link DemoScenario} (the shape
 * ChatScreen consumes, unchanged) from the meta + the translated strings.
 *
 * Adding a new walkthrough is a matter of adding a `ScenarioMeta` entry + its
 * `scenarios.<id>` translation block — no component changes required.
 */

import { useTranslation } from 'react-i18next';

/* ─────────────────────────────────────────────────────────────────────────
 * State machine
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Ordered steps of the demo state machine. Autoplay advances through these on
 * a timer (looping back to `startpage`); interactive mode advances on the
 * correct hotspot click.
 */
export const DEMO_STEPS = [
  'startpage', // StartPage dashboard with workflow tiles
  'chat-typing', // Chat view, question typing into the input
  'user-sent', // user bubble appeared
  'thinking', // ThinkingProcess card stepping through the pipeline
  'answering', // assistant bubble streaming the answer
  'answered', // answer + citations fully shown
  'citation-open', // document viewer panel sliding in
  'verified', // self-check confidence badge shown
] as const;

export type DemoStep = (typeof DEMO_STEPS)[number];

/** Index helper — order is the single source of truth. */
export const stepIndex = (step: DemoStep): number => DEMO_STEPS.indexOf(step);

/** The first step the demo (re)starts at. */
export const FIRST_STEP: DemoStep = DEMO_STEPS[0];

/**
 * Which steps require a user click in interactive mode (the others advance
 * automatically once their animation completes — e.g. typing → sending).
 * Maps the step the user is *on* to the hotspot they must hit to leave it.
 */
export type Hotspot = 'tile' | 'send' | 'citation';

export const STEP_HOTSPOT: Partial<Record<DemoStep, Hotspot>> = {
  startpage: 'tile',
  'chat-typing': 'send',
  answered: 'citation',
};

/* ─────────────────────────────────────────────────────────────────────────
 * Scenario shape
 * ───────────────────────────────────────────────────────────────────────── */

export interface WorkflowTileData {
  readonly id: string;
  /** lucide-react icon name resolved by the component */
  readonly icon: 'Search' | 'MessagesSquare' | 'CalendarCheck' | 'FileText';
  readonly title: string;
  readonly subtitle: string;
  /** The tile that carries the pulsing hotspot / drives the demo forward. */
  readonly hotspot?: boolean;
}

export interface TrustBadge {
  readonly icon: 'Server' | 'ShieldCheck' | 'Lock';
  readonly label: string;
}

export interface ThinkingStepData {
  readonly label: string;
  /** Optional metadata line, mirrors the real product's "N Quellen · …". */
  readonly meta?: string;
}

/**
 * A segment of the streamed answer. `kind: 'text'` is plain prose;
 * `kind: 'cite'` renders an inline [N] citation chip referencing `ref`
 * (1-based, matching the citations array order).
 */
export type AnswerSegment =
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'cite'; readonly ref: number };

export interface CitationData {
  /** 1-based number shown in the chip and source list. */
  readonly n: number;
  readonly document: string;
  readonly page: string;
  /** Sentence highlighted (yellow sweep) in the document viewer. */
  readonly highlight: string;
}

export interface DemoScenario {
  readonly id: string;
  /**
   * Preview-only use case that is not yet shipped. The chat top strip shows a
   * "Vorschau · kommt bald" badge so the walkthrough reads as a future
   * capability rather than a live feature.
   */
  readonly comingSoon?: boolean;
  /** Logo wordmark fallback when no asset is available. */
  readonly wordmark: string;
  /**
   * StartScreen-only chrome. The StartScreen is a separate, self-contained
   * component that does not read these from the scenario, so `useScenario`
   * omits them; they remain here as optional for any hand-written scenario.
   */
  readonly tagline?: string;
  readonly greetingTitle?: string;
  readonly greetingSubtitle?: string;
  readonly tiles?: readonly WorkflowTileData[];
  readonly commandPlaceholder: string;
  readonly trust?: readonly TrustBadge[];

  readonly chatTitle: string;
  /** Question typed into the chat input (typewriter). */
  readonly question: string;
  readonly thinkingSteps: readonly ThinkingStepData[];
  /** Streamed assistant answer, segmented so [N] chips render inline. */
  readonly answer: readonly AnswerSegment[];
  readonly citations: readonly CitationData[];

  /** Document viewer payload (the [1] citation target). */
  readonly viewer: {
    readonly title: string;
    readonly page: string;
    /** Lines of the simulated light PDF page. */
    readonly lines: readonly string[];
    /** Index into `lines` that gets the highlight sweep + [N] marker. */
    readonly highlightLine: number;
    readonly marker: number;
  };

  readonly verifiedBadge: string;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Structural (non-translatable) per-scenario data
 *
 * Everything below is structure only — citation numbers, the viewer page
 * geometry (highlightLine / marker) and the self-check confidence %. The text
 * that pairs with it lives in the `demo` namespace under `scenarios.<id>.*`.
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Non-translatable structural data for one scenario. The translatable strings
 * (chatTitle / question / thinkingSteps / answer / citation document+highlight /
 * viewer title+lines / verifiedBadge) come from `scenarios.<id>.*` in the
 * `demo` i18next namespace.
 */
export interface ScenarioMeta {
  /** Preview-only use case (chat strip shows a "kommt bald" badge). */
  readonly comingSoon?: boolean;
  /** Citation chip number + source page, in render order. */
  readonly citations: readonly { readonly n: number; readonly page: string }[];
  /** Document-viewer geometry (the citation target). */
  readonly viewer: {
    readonly page: string;
    /** Index into the viewer lines that gets the highlight sweep + marker. */
    readonly highlightLine: number;
    /** Citation number shown in the in-document marker. */
    readonly marker: number;
  };
  /** Self-check confidence % carried by the verified badge. */
  readonly verifiedConfidence: number;
}

/**
 * The scenario ids the registry exposes, in canonical order. Callers enumerate
 * these; `SCENARIO_META` and the `scenarios.<id>` translation blocks are keyed
 * by the same ids.
 */
/**
 * Only `knowledge` is a chat walkthrough now. The other use cases each have their
 * own faithful product screen (MeetingScreen, ProposalScreen, EmailScreen,
 * DocAnalysisScreen) routed from {@link BunkerDemoTour}, so their old chat-shaped
 * scenario data was removed.
 */
export const SCENARIO_IDS: string[] = ['knowledge'];

/**
 * Structural data per scenario id. The citation `n` values, the viewer
 * `highlightLine`/`marker` and the confidence % are preserved exactly from the
 * original hand-written scenario objects so the doc-viewer highlight and the
 * clickable citation still target the right line.
 */
export const SCENARIO_META: Record<string, ScenarioMeta> = {
  // Wartungsintervalle Anlage K-300 (the default knowledge / RAG-chat walkthrough)
  knowledge: {
    citations: [
      { n: 1, page: 'S. 47' },
      { n: 2, page: 'S. 12' },
    ],
    viewer: { page: 'Seite 47', highlightLine: 5, marker: 1 },
    verifiedConfidence: 96,
  },
};

/* ─────────────────────────────────────────────────────────────────────────
 * Shared startpage chrome
 *
 * The StartScreen is a separate component; ChatScreen only reads `wordmark`
 * (for the assistant bubble head) and `commandPlaceholder` (for the empty
 * input). These are product-wide constants, not per-scenario text, so they are
 * shared safe values rather than translated per scenario.
 * ───────────────────────────────────────────────────────────────────────── */

const WORDMARK = 'bunker.ai';
const COMMAND_PLACEHOLDER = 'Stellen Sie eine Frage an Ihr Firmenwissen…';

/* ─────────────────────────────────────────────────────────────────────────
 * Concrete fallback scenario — Wartungsintervalle Anlage K-300
 *
 * Used as ChatScreen's default `scenario` prop when none is passed (and as a
 * stable reference in tests). This is a fully literal German object that does
 * NOT depend on the `demo` namespace, so it renders real text even before the
 * namespace is registered. It mirrors the `knowledge` SCENARIO_META entry.
 * ───────────────────────────────────────────────────────────────────────── */

export const wartungScenario: DemoScenario = {
  id: 'knowledge',
  wordmark: WORDMARK,
  commandPlaceholder: COMMAND_PLACEHOLDER,
  chatTitle: 'Chat',
  question: 'Welche Wartungsintervalle gelten für Anlage K-300?',
  thinkingSteps: [
    { label: 'Frage verstehen' },
    { label: 'Kapitel finden' },
    { label: 'Doppelt suchen' },
    { label: 'Nachsortieren' },
  ],
  answer: [
    { kind: 'text', value: 'Die Inspektion ist alle 2.000 Betriebsstunden fällig ' },
    { kind: 'cite', ref: 1 },
    { kind: 'text', value: ', der Ölwechsel alle 4.000 ' },
    { kind: 'cite', ref: 2 },
    { kind: 'text', value: '.' },
  ],
  citations: [
    {
      n: 1,
      document: 'Wartungshandbuch K-300',
      page: 'S. 47',
      highlight: 'Die Inspektion ist alle 2.000 Betriebsstunden durchzuführen.',
    },
    {
      n: 2,
      document: 'Herstellervorgaben',
      page: 'S. 12',
      highlight: 'Ein Ölwechsel ist alle 4.000 Betriebsstunden vorzunehmen.',
    },
  ],
  viewer: {
    title: 'Wartungshandbuch K-300',
    page: 'Seite 47',
    lines: [
      '4.2  Wartungsintervalle der Anlage K-300',
      '',
      'Die nachfolgenden Intervalle gelten für den regulären',
      'Betrieb unter Normalbedingungen gemäß Abschnitt 2.1.',
      '',
      'Die Inspektion ist alle 2.000 Betriebsstunden durchzuführen.',
      'Abweichende Intervalle bei erhöhter Beanspruchung sind',
      'in Tabelle 4.3 aufgeführt. Vor jeder Inspektion ist die',
      'Anlage vom Netz zu trennen und gegen Wiedereinschalten',
      'zu sichern.',
    ],
    highlightLine: 5,
    marker: 1,
  },
  verifiedBadge: 'Selbstprüfung bestanden — Konfidenz 96%',
};

/* ─────────────────────────────────────────────────────────────────────────
 * Answer parser
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Parse a single answer string carrying inline full-width citation markers of
 * the form 【n】 into the {@link AnswerSegment}[] ChatScreen renders: plain runs
 * become `{ kind: 'text' }` and each 【n】 becomes `{ kind: 'cite', ref: n }`.
 */
export function parseAnswer(raw: string): AnswerSegment[] {
  const segments: AnswerSegment[] = [];
  const re = /【(\d+)】/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', value: raw.slice(lastIndex, match.index) });
    }
    segments.push({ kind: 'cite', ref: Number(match[1]) });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < raw.length) {
    segments.push({ kind: 'text', value: raw.slice(lastIndex) });
  }
  return segments;
}

/* ─────────────────────────────────────────────────────────────────────────
 * useScenario — assemble a DemoScenario from meta + translated strings
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Assemble the full {@link DemoScenario} ChatScreen consumes from the structural
 * {@link SCENARIO_META} entry plus the translated strings under
 * `scenarios.<id>.*` in the `demo` namespace.
 *
 * Unknown ids fall back to `knowledge` so callers can pass through untrusted ids
 * (matching the old `SCENARIOS[id] ?? SCENARIOS.knowledge` behaviour).
 */
export function useScenario(id: string): DemoScenario {
  const { t } = useTranslation('demo');
  const metaId = SCENARIO_META[id] ? id : 'knowledge';
  const meta = SCENARIO_META[metaId];

  const thinkingSteps = (
    t(`scenarios.${metaId}.thinkingSteps`, { returnObjects: true }) as string[]
  ).map((label) => ({ label }));

  const answer = parseAnswer(t(`scenarios.${metaId}.answer`));

  const citations: CitationData[] = meta.citations.map((c, i) => ({
    n: c.n,
    page: c.page,
    document: t(`scenarios.${metaId}.citations.${i}.document`),
    highlight: t(`scenarios.${metaId}.citations.${i}.highlight`),
  }));

  return {
    id: metaId,
    comingSoon: meta.comingSoon,
    wordmark: WORDMARK,
    commandPlaceholder: COMMAND_PLACEHOLDER,
    chatTitle: t(`scenarios.${metaId}.chatTitle`),
    question: t(`scenarios.${metaId}.question`),
    thinkingSteps,
    answer,
    citations,
    viewer: {
      title: t(`scenarios.${metaId}.viewerTitle`),
      page: meta.viewer.page,
      lines: t(`scenarios.${metaId}.viewerLines`, { returnObjects: true }) as string[],
      highlightLine: meta.viewer.highlightLine,
      marker: meta.viewer.marker,
    },
    verifiedBadge: t(`scenarios.${metaId}.verifiedBadge`),
  };
}
